import i18n from 'i18next';
import isEmpty from 'lodash/isEmpty';
import { action, autorun, makeObservable, observable, computed } from 'mobx';

import type { SupportedLanguages } from '../../i18n/constants';
import { getUserLanguage } from '../../i18n/helpers';
import { getRequest, postRequest } from '../../services/api';
import { ldClient, resolveFlag } from '../../services/launchDarkly';
import { devError } from '../../utils/log';
import { singleFlight } from '../../utils/singleFlight';
import type { FeatureFetchStatus, FeatureManagerDialogType, IFeatureManagerStore } from '../feature-manager/types';
import { type RootStore } from '../RootStore';
import { Loadable } from '../traits/Loadable';
import { EnumOpenDialog } from '../ui/types';

import type {
  DialogMarketingConsent,
  DialogMarketingConsentDTO,
  PhoneVerificationMarketingConsent,
  PhoneVerificationMarketingConsentDTO,
  SignUpMarketingConsent,
  SignUpMarketingConsentDTO,
} from './MarketingConsentStore.types';

export class MarketingConsentStore extends Loadable implements IFeatureManagerStore {
  private root: RootStore;
  private prevLang = getUserLanguage();

  constructor(_rootStore: RootStore) {
    super();
    this.root = _rootStore;
    makeObservable(this);

    this.marketingConsentFlag = resolveFlag('marketing_consent', true);

    autorun(() => void this.fetchSignUpMarketingConsent());

    i18n.on('languageChanged', (rawLang) => {
      const lang = getUserLanguage(rawLang);

      if (this.prevLang !== lang) {
        this.prevLang = lang;
        void this.fetchSignUpMarketingConsent(lang);
      }
    });

    ldClient?.on('ready', () => {
      this.marketingConsentFlag = resolveFlag('marketing_consent', true);
    });

    ldClient?.on('change', (changes) => {
      if ('marketing_consent' in changes) {
        this.updateMarketingConsentFlag(changes.marketing_consent.current);
      }
    });
  }

  @observable
  public marketingConsentFlag: boolean = false;

  @observable
  public signUpMarketingConsents: SignUpMarketingConsent[] = [];

  @observable
  public phoneVerificationMarketingConsents: PhoneVerificationMarketingConsent[] = [];

  @observable
  public marketingConsents: DialogMarketingConsent[] = [];

  @observable
  public currentConsentIndex: number = 0;

  public dialogTypes: FeatureManagerDialogType[] = ['MarketingConsent'];

  @observable
  private fetchingUserMarketingConsents = false;

  @observable
  public fetchingSignUpMarketingConsent = false;

  @observable
  public fetchingPhoneVerificationMarketingConsent = false;

  @action
  private updateMarketingConsentFlag(value: boolean) {
    this.marketingConsentFlag = value;
  }

  @action
  public setFetchingUserMarketingConsents(value: boolean) {
    this.fetchingUserMarketingConsents = value;
  }

  @action
  public setFetchingPhoneVerificationMarketingConsent(value: boolean) {
    this.fetchingPhoneVerificationMarketingConsent = value;
  }

  @action
  public setPhoneVerificationMarketingConsents(value: PhoneVerificationMarketingConsent[]) {
    this.phoneVerificationMarketingConsents = value;
  }

  @action
  public setFetchingSignUpMarketingConsent(value: boolean) {
    this.fetchingSignUpMarketingConsent = value;
  }

  @action
  public setSignUpMarketingConsents(value: SignUpMarketingConsent[]) {
    this.signUpMarketingConsents = value;
  }

  @action
  public setUserMarketingConsents(value: DialogMarketingConsent[]) {
    this.marketingConsents = value;
  }

  @observable
  public marketingConsentAnswers: Record<string, boolean> = {};

  @action
  public setMarketingConsentAnswers(consents: Record<string, boolean>) {
    this.marketingConsentAnswers = { ...this.marketingConsentAnswers, ...consents };
  }

  @computed
  public get currentConsent(): DialogMarketingConsent | null {
    if (isEmpty(this.marketingConsents)) {
      return null;
    }

    if (this.currentConsentIndex >= this.marketingConsents.length) {
      return null;
    }

    return this.marketingConsents[this.currentConsentIndex];
  }

  @computed
  public get hasMoreConsents(): boolean {
    return this.currentConsentIndex < this.marketingConsents.length - 1;
  }

  public startDialogs() {
    if (!isEmpty(this.marketingConsents)) {
      this.root.uiStore.setOpenDialog({ dialog: EnumOpenDialog.isOpenMarketingConsentDialog });

      return true;
    }
    return false;
  }

  public fetch = singleFlight(async (): Promise<FeatureFetchStatus> => {
    if (!this.marketingConsentFlag) return 'OFF';

    try {
      this.setFetchingSignUpMarketingConsent(true);

      const marketingConsents = await this.fetchMarketingConsent();
      this.setUserMarketingConsents(marketingConsents);

      return 'READY';
    } catch (error) {
      devError('MarketingConsentStore.fetch', error);
      return 'ERROR';
    } finally {
      this.setFetchingSignUpMarketingConsent(false);
    }
  });

  public async fetchMarketingConsent() {
    try {
      this.setFetchingUserMarketingConsents(true);
      const {
        data: { marketingConsents },
      } = await getRequest<{ data: DialogMarketingConsentDTO }>('marketing-consent', { lang: getUserLanguage() });

      return marketingConsents;
    } catch (error) {
      devError('MarketingConsentStore.fetchMarketingConsent', error);

      return [];
    } finally {
      this.setFetchingUserMarketingConsents(false);
    }
  }

  public async fetchSignUpMarketingConsent(lang?: SupportedLanguages) {
    try {
      this.setFetchingSignUpMarketingConsent(true);

      const {
        data: { marketingConsents },
      } = await getRequest<{ data: SignUpMarketingConsentDTO }>('marketing-consent/sign_up', {
        lang: lang || getUserLanguage(),
      });

      this.setSignUpMarketingConsents(marketingConsents);
    } catch (error) {
      devError('MarketingConsentStore.fetchSignUpMarketingConsent', error);
    } finally {
      this.setFetchingSignUpMarketingConsent(false);
    }
  }

  public async fetchPhoneVerificationConsent(lang?: SupportedLanguages) {
    try {
      this.setFetchingPhoneVerificationMarketingConsent(true);
      const {
        data: { marketingConsents },
      } = await getRequest<{ data: PhoneVerificationMarketingConsentDTO }>('marketing-consent/phone_verification', {
        lang: lang || getUserLanguage(),
      });

      this.setPhoneVerificationMarketingConsents(marketingConsents);
    } catch (error) {
      devError('MarketingConsentStore.fetchPhoneVerificationConsent', error);
    } finally {
      this.setFetchingPhoneVerificationMarketingConsent(false);
    }
  }

  public async acknowledgeMarketingConsent(marketingConsentId: string, isAccepted: boolean) {
    try {
      const { referrer } = document;
      await postRequest('marketing-consent', { marketingConsentId, isAccepted, referrer });
    } catch (error) {
      devError('MarketingConsentStore.acknowledgeMarketingConsent', error);
    }
  }

  public async closeMarketingConsentDialog(marketingConsentId: string, isAccepted: boolean) {
    try {
      await this.acknowledgeMarketingConsent(marketingConsentId, isAccepted);
    } catch (error) {
      devError('MarketingConsentStore.closeMarketingConsentDialog', error);
    } finally {
      if (!this.hasMoreConsents) {
        this.root.featureManagerStore.onDialogFinished('MarketingConsent');
        this.root.uiStore.setCloseDialog();
      }
      this.currentConsentIndex += 1;
    }
  }

  public async postMarketingConsents(marketingConsents: Record<string, boolean>) {
    try {
      await postRequest('/v2/marketing-consent', { marketingConsents });
    } catch (error) {
      devError('MarketingConsentStore.postMarketingConsents', error);
    }
  }
}
